import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { setUserInfo } from '../store/slices/userInfoSlice';
import { popLogin } from '../store/slices/loginsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';
import { clearHideableColumnManagerState } from '../utils/HideableColumnManager';

import DA from '../store/ReduxAccess';
import ReduxLoader from '../store/ReduxLoader';

class Logout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      redirect: undefined,
      errorMessage: '',
      toHome: false,
      loggedOut: false,
      loggingBackIn: false,
    };
  }

  mimicLoggingInTextually(email, password) {
    const credentialsObj = { email, password };
    const uri = '/api/account/logInTest';
    const request = createStdRequest(uri, 'POST', credentialsObj);
    const actionOnGoodResponse = (json) => {
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, errorCode } = json;
      if (success) {
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);

        if (role === 'User' || role === 'Admin') {
          ReduxLoader.loadFirmInfo(firmId, this, () => this.setState({ toHome: true, loggingBackIn: false }));
        } else if (role === 'SuperAdmin' || role === 'AllevaRep') {
          // These users have no "firm" and trying to fetch full firm info would fail.
          // Presumably, they will later log in via a Demo or Firm overview as a regular user
          const firmInfo = { firmId: 0, firmName: '' };
          ReduxLoader.setFirmInfoAndNotifyWebSocket(firmInfo);
          ReduxLoader.quasiLoadStateForNoFirm();
          this.setState({ toHome: true, loggingBackIn: false });
        } else {
          this.setState({
            errorMessage: "You appear to be able to log in, but have no 'role.' Our DB must be messed up.",
          });
          this.setState({ toHome: true, loggingBackIn: false });
        }
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but bad password
        //   3) Requires 2-factor auth
        //   4) User is locked out
        //   5) Account has been deactivated
        switch (errorCode) {
          case 1:
            this.setState({ errorMessage: 'We have no account with that user name (email)' });
            break;
          case 2:
            this.setState({ errorMessage: 'The provided password does not match the user' });
            break;
          case 3:
            this.setState({ errorMessage: 'This account requires 2-factor authentication' });
            break;
          case 4:
            this.setState({ errorMessage: 'This user account is locked out' });
            break;
          case 5:
            this.setState({ errorMessage: 'This account has been deactivated' });
            break;
          default:
            this.setState({ errorMessage: 'Unknown login error. Error code: ' + errorCode });
            break;
        }
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  mimicLoggingInToFirm(email) {
    const uri = '/api/account/logInToFirm?email=' + encodeURIComponent(email);
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, errorCode } = json;
      if (success) {
        // put the response's new user info into Redux state
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);
        ReduxLoader.loadFirmInfo(firmId, this, () => this.setState({ toHome: true, loggingBackIn: false }));
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but not attached to a provider
        //   5) Firm has been deactivated
        switch (errorCode) {
          case 1:
            this.setState({ errorMessage: 'We have no account with that user name (email)' });
            break;
          case 2:
            this.setState({ errorMessage: 'The user is not associated with a provider' });
            break;
          case 5:
            this.setState({ errorMessage: 'The Account has been deactivated' });
            break;
          default:
            this.setState({ errorMessage: 'Unknown login error. Error code: ' + errorCode });
            break;
        }
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  mimicLoggingInToDemo(email) {
    const uri = '/api/Account/LogInToDemo?email=' + encodeURIComponent(email);
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, errorCode } = json;
      if (success) {
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);
        ReduxLoader.loadFirmInfo(firmId, this, () => this.setState({ toHome: true, loggingBackIn: false }));
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but not attached to a provider
        //   3) The associated provider is not part of a demo
        //   5) The demo firm has been deactivated
        switch (errorCode) {
          case 1:
            this.setState({ errorMessage: 'We have no account with that user name (email)' });
            break;
          case 2:
            this.setState({ errorMessage: 'The user is not associated with a provider' });
            break;
          case 3:
            this.setState({ errorMessage: "There is no demo associated with this user's provider" });
            break;
          case 5:
            this.setState({ errorMessage: 'This demo has been deactivated' });
            break;
          default:
            this.setState({ errorMessage: 'Unknown login error. Error code: ' + errorCode });
            break;
        }
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  mimicLoggingInAsDev() {
    const uri = '/api/Account/LoginAsDev';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, firmName, errorDescrip } =
        json;
      if (success) {
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        const firmInfo = { firmId, firmName };
        this.props.setUserInfo(userInfo);
        ReduxLoader.setFirmInfoAndNotifyWebSocket(firmInfo);
        ReduxLoader.quasiLoadStateForNoFirm();
        this.setState({ toHome: true, loggingBackIn: false });
      } else {
        this.setState({ errorMessage: errorDescrip });
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildApiStructForLogout() {
    const settingsForUserWhosLoggingOut = DA.getAllLoggedInUserSettings();
    const settingsJson = JSON.stringify(settingsForUserWhosLoggingOut);
    return {
      userId: DA.getLoggedInUserId(),
      settingsJson: settingsJson,
    };
  }

  clearMostState() {
    DA.clearStore(); // clears all Redux state except logins
    clearHideableColumnManagerState();
  }

  componentDidMount() {
    this.props.popLogin();
    const loginObjToRevertTo = DA.getTopLogin();
    const uri = '/api/Account/Logout';
    const apiStruct = this.buildApiStructForLogout();
    const request = createStdRequest(uri, 'post', apiStruct);
    const actionOnGoodResponse = (json) => {
      const { success } = json;
      if (success) {
        this.clearMostState(); // clears everything but logins
        if (loginObjToRevertTo === null) {
          this.setState({ loggedOut: true });
        } else {
          const { loginType, email, password } = loginObjToRevertTo;
          switch (loginType) {
            case 'textual':
              this.mimicLoggingInTextually(email, password);
              break;
            case 'toFirm':
              this.mimicLoggingInToFirm(email);
              break;
            case 'toDemo':
              this.mimicLoggingInToDemo(email);
              break;
            case 'asDev':
              this.mimicLoggingInAsDev();
              break;
            default:
              break;
          }
        }
      }
    };
    if (loginObjToRevertTo !== null) {
      this.setState({ loggingBackIn: true });
    }
    apiFetch(this, request, actionOnGoodResponse);
  }

  render() {
    if (this.state.errorMessage) {
      return (
        <div>
          <h3>Encountered Error</h3>
          <div style={{ marginLeft: '20px', color: 'red' }}>{this.state.errorMessage}</div>
        </div>
      );
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.loggingBackIn) {
      return <h3>We're logging you back in as the previous user</h3>;
    }

    if (this.state.toHome || this.state.loggedOut) {
      return <Redirect to="/" />;
    }

    return <h3>Shouldn't spend too long here!</h3>;
  }
}

export default connect(null, { setUserInfo, popLogin, setApiResponseInfo })(Logout);
